<template>
  <div class="login-container">
    <el-card class="login-card">
      <div slot="header" class="clearfix">
        <span style="font-size: 18px; font-weight: bold;">{{ isRegister ? '用户注册' : '系统登录' }}</span>
      </div>
      
      <!-- 登录表单 -->
      <el-form 
        v-if="!isRegister"
        :model="loginForm" 
        :rules="loginRules" 
        ref="loginForm" 
        label-width="0"
      >
        <el-form-item prop="username">
          <el-input 
            v-model="loginForm.username" 
            prefix-icon="el-icon-user" 
            placeholder="用户名"
          ></el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input 
            type="password" 
            v-model="loginForm.password" 
            prefix-icon="el-icon-lock" 
            placeholder="密码"
            @keyup.enter.native="handleLogin"
          ></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleLogin" :loading="loading" style="width: 100%">登录</el-button>
        </el-form-item>
        <el-form-item class="switch-text">
          <span>还没有账号？<a href="javascript:void(0)" @click="isRegister = true">立即注册</a></span>
        </el-form-item>
      </el-form>

      <!-- 注册表单 -->
      <el-form 
        v-else
        :model="registerForm" 
        :rules="registerRules" 
        ref="registerForm" 
        label-width="0"
      >
        <el-form-item prop="username">
          <el-input 
            v-model="registerForm.username" 
            prefix-icon="el-icon-user" 
            placeholder="用户名 (3-20位)"
          ></el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input 
            type="password" 
            v-model="registerForm.password" 
            prefix-icon="el-icon-lock" 
            placeholder="密码 (6-20位)"
          ></el-input>
        </el-form-item>
        <el-form-item prop="confirmPassword">
          <el-input 
            type="password" 
            v-model="registerForm.confirmPassword" 
            prefix-icon="el-icon-lock" 
            placeholder="确认密码"
            @keyup.enter.native="handleRegister"
          ></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="success" @click="handleRegister" :loading="registerLoading" style="width: 100%">注册</el-button>
        </el-form-item>
        <el-form-item class="switch-text">
          <span>已有账号？<a href="javascript:void(0)" @click="isRegister = false">立即登录</a></span>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { login, register } from '@/api/modules/auth'
import { initDynamicRoutes } from '@/router'

export default {
  name: 'Login',
  data() {
    // 确认密码校验规则
    const validateConfirmPassword = (_rule, value, callback) => {
      if (value !== this.registerForm.password) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    return {
      // 登录/注册切换
      isRegister: false,
      
      // 登录表单
      loginForm: {
        username: '',
        password: ''
      },
      loginRules: {
        username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
      },
      loading: false,
      
      // 注册表单
      registerForm: {
        username: '',
        password: '',
        confirmPassword: ''
      },
      registerRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度需在 3-20 个字符之间', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度需在 6-20 个字符之间', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      },
      registerLoading: false
    }
  },
  methods: {
    /**
     * 统一兼容新旧登录接口响应格式。
     * @param {{success?: boolean, code?: number, message?: string, token?: string, user?: Object, data?: {token?: string, user?: Object}}} responseData 登录接口原始响应数据。
     * @returns {{isLoginSuccess: boolean, token: string, user: Object, message: string}}
     */
    normalizeLoginResponse(responseData) {
      /**
       * 登录接口返回的外层数据。
       * @type {{success?: boolean, code?: number, message?: string, token?: string, user?: Object, data?: {token?: string, user?: Object}}}
       */
      const responsePayload = responseData || {}
      /**
       * 新接口把 token 和用户信息放在 data 内，这里做一层兜底读取。
       * @type {{token?: string, user?: Object}}
       */
      const responseInnerData = responsePayload.data || {}
      /**
       * 当前登录接口是否表示成功，兼容 success=true、code=0 和 code=200。
       * @type {boolean}
       */
      const isResponseSuccess = responsePayload.success === true || String(responsePayload.code) === '0' || String(responsePayload.code) === '200'
      /**
       * 当前登录成功后用于鉴权的 token。
       * @type {string}
       */
      const token = responsePayload.token || responseInnerData.token || ''
      /**
       * 当前登录成功后需要缓存的用户信息。
       * @type {Object}
       */
      const user = responsePayload.user || responseInnerData.user || {}

      return {
        isLoginSuccess: Boolean(isResponseSuccess && token),
        token,
        user,
        message: isResponseSuccess && !token ? '登录成功但未返回 token' : (responsePayload.message || '登录失败')
      }
    },
    // 登录
    handleLogin() {
      this.$refs.loginForm.validate(async valid => {
        if (valid) {
          this.loading = true
          try {
            const responseData = await login(this.loginForm.username, this.loginForm.password)
            const loginResult = this.normalizeLoginResponse(responseData)
            if (loginResult.isLoginSuccess) {
              localStorage.setItem('token', loginResult.token)
              localStorage.setItem('user', JSON.stringify(loginResult.user))
              /**
               * 登录成功后立即初始化动态路由，避免先跳进系统再被路由守卫判定为路由未就绪。
               */
              await initDynamicRoutes(true)
              this.$toast.success('登录成功')
              this.$router.replace('/home')
            } else {
              this.$toast.error(loginResult.message)
            }
          } catch (error) {
            this.$toast.error(error.message || '登录失败')
          } finally {
            this.loading = false
          }
        }
      })
    },

    // 注册
    handleRegister() {
      this.$refs.registerForm.validate(async valid => {
        if (valid) {
          this.registerLoading = true
          try {
            const responseData = await register(this.registerForm.username, this.registerForm.password)
            if (responseData.success) {
              this.$toast.success('注册成功，请登录')
              // 切换到登录页并填充用户名
              this.isRegister = false
              this.loginForm.username = this.registerForm.username
              this.loginForm.password = ''
              // 清空注册表单
              this.registerForm = {
                username: '',
                password: '',
                confirmPassword: ''
              }
            } else {
              this.$toast.error(responseData.message || '注册失败')
            }
          } catch (error) {
            this.$toast.error(error.message || '注册失败')
          } finally {
            this.registerLoading = false
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.login-container {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #2d3a4b 0%, #1a2a3a 100%);
}
.login-card {
  width: 380px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}
.switch-text {
  text-align: center;
  margin-bottom: 0;
}
.switch-text span {
  color: #606266;
  font-size: 14px;
}
.switch-text a {
  color: #409eff;
  text-decoration: none;
}
.switch-text a:hover {
  text-decoration: underline;
}
</style>
